// ============================================================================
//  CRM Phase 5-B — Pages Objectifs & Séquences drip
//  À ajouter dans apps/frontend/src/pages/crm/CrmPages.tsx
//  (ou importer depuis ce fichier séparé)
// ============================================================================

import React, { useState, useMemo } from 'react'
import ReactDOM from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Target, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2,
  Plus, Trash2, Edit2, Play, Square, ChevronDown, ChevronUp,
  Mail, Zap, Clock, Users, MoreVertical, X, Eye, Copy,
  ArrowRight, Flame, Calendar, BarChart3, RefreshCw,
} from 'lucide-react'
import { crmApi, type SalesTarget, type EmailSequence } from '@/modules/crm/api/crm.api'
import { useNotificationStore } from '@/stores/notification.store'
import { useAuthStore }         from '@/stores/auth.store'
import { EmployeeCombobox } from '@/modules/crm/components/CrmComboboxes'

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtMoney = (n?: number, currency = 'XOF') => {
  if (!n && n !== 0) return '—'
  return new Intl.NumberFormat('fr-SN', { style: 'currency', currency, minimumFractionDigits: 0 }).format(n)
}

const Spinner = () => (
  <div className="w-5 h-5 border-2 border-[var(--blue)] border-t-transparent rounded-full animate-spin" />
)

const inputCls = "w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)] placeholder:text-[var(--tx3)]"

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">{label}</label>
      {children}
    </div>
  )
}

function Modal({ title, onClose, children, wide }: {
  title: string; onClose: () => void; children: React.ReactNode; wide?: boolean
}) {
  return (
    <div className="fixed inset-0 bg-[rgba(13,8,32,0.50)] backdrop-blur-sm flex items-center justify-center z-[99999] p-4">
      <div className={`bg-white border border-[var(--bd)] rounded-2xl w-full shadow-2xl max-h-[90vh] flex flex-col ${wide ? 'max-w-2xl' : 'max-w-lg'}`}>
        <div className="flex items-center justify-between p-5 border-b border-[var(--bd)] shrink-0">
          <h2 className="text-base font-semibold text-[var(--tx)]">{title}</h2>
          <button onClick={onClose}><X className="w-4 h-4 text-[var(--tx3)] hover:text-[var(--tx)]" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">{children}</div>
      </div>
    </div>
  )
}

// ─── Status config ────────────────────────────────────────────────────────────

const TARGET_STATUS: Record<string, { label: string; color: string; bg: string; icon: typeof CheckCircle2 }> = {
  ACHIEVED: { label: 'Atteint',    color: 'text-[var(--green-text)]', bg: 'bg-[var(--green)]/10', icon: CheckCircle2 },
  ON_TRACK: { label: 'En bonne voie', color: 'text-[var(--blue-d)]',    bg: 'bg-[#38BDF8]/10',    icon: TrendingUp },
  AT_RISK:  { label: 'En retard',  color: 'text-[var(--amber-text)]',   bg: 'bg-[var(--amber)]/10',   icon: AlertTriangle },
  BELOW:    { label: 'En retard',  color: 'text-[var(--red-text)]',     bg: 'bg-[#F87171]/10',     icon: TrendingDown },
}

const TARGET_TYPES = ['REVENUE', 'DEALS', 'ACTIVITIES', 'CALLS', 'MEETINGS'] as const
const TARGET_PERIODS = ['WEEKLY', 'MONTHLY', 'QUARTERLY', 'ANNUAL'] as const

const TARGET_TYPE_LABELS: Record<string, string> = {
  REVENUE: 'Chiffre d\'affaires', DEALS: 'Deals gagnés',
  ACTIVITIES: 'Activités', CALLS: 'Appels', MEETINGS: 'Réunions',
}

const PERIOD_LABELS: Record<string, string> = { MONTHLY: 'Mensuel', WEEKLY: 'Hebdomadaire', QUARTERLY: 'Trimestriel', ANNUAL: 'Annuel' }

const MONTH_NAMES = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc']

// ============================================================================
//  PAGE OBJECTIFS COMMERCIAUX
// ============================================================================

export function TargetsPage() {
  const [showCreate, setShowCreate]   = useState(false)
  const [yearFilter, setYearFilter]   = useState(new Date().getFullYear())
  const [typeFilter, setTypeFilter]   = useState<string>('')
  const [expanded, setExpanded]       = useState<string | null>(null)
  const queryClient = useQueryClient()
  const addNotif    = useNotificationStore(s => s.addNotification)
  const { hasPermission, user } = useAuthStore()
  const canManage = hasPermission('crm:configure')

  const { data: targetsData, isLoading, refetch } = useQuery({
    queryKey: ['crm-targets', yearFilter, typeFilter],
    queryFn:  () => crmApi.listTargets({ year: yearFilter, type: typeFilter || undefined }),
    refetchInterval: 60_000, // refresh auto toutes les minutes
  })
  const data: SalesTarget[] = targetsData?.items ?? []

  const deleteMutation = useMutation({
    mutationFn: (id: string) => crmApi.deleteTarget(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-targets'] })
      addNotif({ id: Date.now().toString(), type: 'success', title: 'Objectif supprimé' })
    },
  })

  // Grouper par commercial
  const grouped = useMemo(() => {
    const map: Record<string, SalesTarget[]> = {}
    for (const t of data) {
      const key = t.employeeId
      if (!map[key]) map[key] = []
      map[key].push(t)
    }
    return Object.entries(map)
  }, [data])

  const currentYear = new Date().getFullYear()

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[var(--tx)]">Objectifs commerciaux</h1>
          <p className="text-[var(--tx3)] text-xs mt-0.5">Progression temps réel · Alertes mi-période automatiques</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => refetch()} className="p-2 text-[var(--tx3)] hover:text-[var(--tx)] hover:bg-[var(--surface-2)] rounded-lg transition-colors">
            <RefreshCw className="w-4 h-4" />
          </button>
          {canManage && (
            <button onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-xl text-sm font-medium transition-colors">
              <Plus className="w-4 h-4" /> Nouvel objectif
            </button>
          )}
        </div>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap gap-2">
        <select value={yearFilter} onChange={e => setYearFilter(Number(e.target.value))}
          className="px-3 py-2 nx-card text-sm text-[var(--tx)] focus:outline-none">
          {[currentYear - 1, currentYear, currentYear + 1].map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
          className="px-3 py-2 nx-card text-sm text-[var(--tx)] focus:outline-none">
          <option value="">Toutes les métriques</option>
          {TARGET_TYPES.map(t => <option key={t} value={t}>{TARGET_TYPE_LABELS[t]}</option>)}
        </select>
      </div>

      {/* Résumé global */}
      {data.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: 'Total objectifs', value: data.length, icon: Target, color: 'text-[var(--blue-d)]', bg: 'bg-[#38BDF8]/10' },
            { label: 'Atteints', value: data.filter(t => t.status === 'ACHIEVED').length, icon: CheckCircle2, color: 'text-[var(--green-text)]', bg: 'bg-[var(--green)]/10' },
            { label: 'En bonne voie', value: data.filter(t => t.status === 'ON_TRACK').length, icon: TrendingUp, color: 'text-[var(--blue-d)]', bg: 'bg-[#38BDF8]/10' },
            { label: 'En retard / À risque', value: data.filter(t => t.status === 'AT_RISK' || t.status === 'BELOW').length, icon: AlertTriangle, color: 'text-[var(--amber-text)]', bg: 'bg-[var(--amber)]/10' },
          ].map(k => (
            <div key={k.label} className="nx-card p-5 flex items-center gap-4 shadow-sm hover:shadow-md transition-shadow" style={{borderTop:`3px solid ${(k.color.match(/#[0-9A-Fa-f]{3,6}/g)?.[0]??'var(--brand-orange)')}`}}>
              <div className={`p-2 ${k.bg} rounded-lg shrink-0`}><k.icon className={`w-5 h-5 ${k.color}`} /></div>
              <div>
                <p className="text-2xl font-bold text-[var(--tx)] leading-none">{k.value}</p>
                <p className="text-xs text-[var(--tx3)]">{k.label}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Liste groupée par commercial */}
      {isLoading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : grouped.length === 0 ? (
        <div className="bg-[var(--surface)] border border-[var(--bd)] rounded-xl p-12 text-center">
          <Target className="w-10 h-10 text-[var(--tx4)] mx-auto mb-3" />
          <p className="text-[var(--tx3)] text-sm">Aucun objectif pour {yearFilter}</p>
          {canManage && (
            <button onClick={() => setShowCreate(true)}
              className="mt-4 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm">
              Créer le premier objectif
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {grouped.map(([empId, targets]) => {
            const emp   = targets[0]?.employee
            const name  = emp ? `${emp.user.firstName} ${emp.user.lastName}` : empId
            const isExp = expanded === empId
            const atRisk = targets.filter(t => t.isBehind).length

            return (
              <div key={empId} className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl overflow-hidden">
                {/* En-tête commercial */}
                <button
                  className="w-full flex items-center gap-3 px-5 py-4 hover:bg-[var(--surface-2)] transition-colors text-left"
                  onClick={() => setExpanded(isExp ? null : empId)}
                >
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-600 to-primary-500 flex items-center justify-center text-[var(--tx)] text-sm font-bold shrink-0">
                    {name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[var(--tx)]">{name}</p>
                    <p className="text-xs text-[var(--tx3)]">{targets.length} objectif{targets.length > 1 ? 's' : ''}</p>
                  </div>
                  {atRisk > 0 && (
                    <div className="flex items-center gap-1 px-2 py-0.5 bg-[var(--amber)]/10 rounded-full">
                      <AlertTriangle className="w-3 h-3 text-[var(--amber-text)]" />
                      <span className="text-xs text-[var(--amber-text)]">{atRisk} en retard</span>
                    </div>
                  )}
                  {isExp ? <ChevronUp className="w-4 h-4 text-[var(--tx3)] shrink-0" /> : <ChevronDown className="w-4 h-4 text-[var(--tx3)] shrink-0" />}
                </button>

                {/* Objectifs du commercial */}
                {isExp && (
                  <div className="border-t border-[var(--bd)] divide-y divide-[var(--bd)]">
                    {targets.map(t => <TargetRow key={t.id} target={t} canManage={canManage} onDelete={() => deleteMutation.mutate(t.id)} />)}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {showCreate && <CreateTargetModal onClose={() => setShowCreate(false)} onCreated={() => {
        queryClient.invalidateQueries({ queryKey: ['crm-targets'] })
        setShowCreate(false)
        addNotif({ id: Date.now().toString(), type: 'success', title: 'Objectif créé' })
      }} />}
    </div>
  )
}

// ─── Ligne objectif ───────────────────────────────────────────────────────────

function TargetRow({ target: t, canManage, onDelete }: {
  target: SalesTarget; canManage: boolean; onDelete: () => void
}) {
  const cfg       = TARGET_STATUS[t.status ?? 'BELOW']
  const StatusIcon = cfg?.icon ?? AlertTriangle
  const progress  = t.progressRate ?? 0
  const barColor  = t.status === 'ACHIEVED' ? '#34d399'
                  : t.status === 'ON_TRACK'  ? '#60a5fa'
                  : t.status === 'AT_RISK'   ? '#fbbf24'
                  : '#f87171'

  const periodLabel = t.type === 'MONTHLY'
    ? `${MONTH_NAMES[(t.period ?? 1) - 1]} ${t.year}`
    : t.type === 'QUARTERLY'
    ? `T${t.period} ${t.year}`
    : t.type === 'ANNUAL'
    ? `${t.year}`
    : `S${t.period} ${t.year}`

  const valueLabel = t.targetType === 'REVENUE'
    ? `${fmtMoney(t.actualValue, t.currency)} / ${fmtMoney(t.targetValue, t.currency)}`
    : `${t.actualValue ?? 0} / ${t.targetValue}`

  return (
    <div className="px-5 py-4">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-[var(--tx)]">{TARGET_TYPE_LABELS[t.targetType]}</span>
            <span className="text-xs px-2 py-0.5 bg-[var(--surface-2)] text-[var(--tx2)] rounded-full">{PERIOD_LABELS[t.type]}</span>
            <span className="text-xs text-[var(--tx3)]">{periodLabel}</span>
            {t.isBehind && (
              <span className="flex items-center gap-1 text-xs px-2 py-0.5 bg-[var(--amber)]/10 text-[var(--amber-text)] rounded-full">
                <AlertTriangle className="w-2.5 h-2.5" /> Mi-période dépassée
              </span>
            )}
          </div>
          <p className="text-sm text-[var(--tx3)] mt-0.5">{valueLabel}</p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {cfg && (
            <div className={`flex items-center gap-1 px-2 py-1 ${cfg.bg} rounded-lg`}>
              <StatusIcon className={`w-3 h-3 ${cfg.color}`} />
              <span className={`text-xs font-medium ${cfg.color}`}>{cfg.label}</span>
            </div>
          )}
          {canManage && (
            <button onClick={onDelete}
              className="p-1.5 text-[var(--tx3)] hover:text-[var(--red-text)] hover:bg-[#F87171]/10 rounded-lg transition-colors">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Barre de progression */}
      <div className="space-y-1">
        <div className="flex items-center justify-between text-xs text-[var(--tx3)]">
          <span>Progression : {progress}%</span>
          {t.periodProgress !== undefined && (
            <span>Période : {t.periodProgress}% écoulée</span>
          )}
        </div>
        <div className="relative h-2.5 bg-[var(--surface-2)] rounded-full overflow-hidden">
          {/* Marqueur mi-période */}
          {t.type === 'MONTHLY' && (
            <div className="absolute top-0 left-1/2 w-px h-full bg-[var(--surface-3)] z-10" />
          )}
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{ width: `${Math.min(100, progress)}%`, backgroundColor: barColor }}
          />
        </div>
      </div>
    </div>
  )
}

// ─── Modal création objectif ──────────────────────────────────────────────────

function CreateTargetModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const now = new Date()
  const [form, setForm] = useState({
    employeeId: '',
    type: 'MONTHLY',
    year: now.getFullYear(),
    period: now.getMonth() + 1,
    targetType: 'REVENUE',
    targetValue: '',
    currency: 'XOF',
  })
  const f = (k: string) => (e: any) => setForm(p => ({ ...p, [k]: e.target.value }))

  const { mutate, isPending } = useMutation({
    mutationFn: () => crmApi.createTarget({
      ...form,
      year:        Number(form.year),
      period:      Number(form.period),
      targetValue: Number(form.targetValue),
    }),
    onSuccess: onCreated,
  })

  const isValid = form.employeeId && form.targetValue && Number(form.targetValue) > 0

  return (
    <Modal title="Nouvel objectif commercial" onClose={onClose}>
      <Field label="Commercial *">
        <EmployeeCombobox ariaLabel="Commercial" required value={form.employeeId || null} onChange={id => setForm(p => ({ ...p, employeeId: id ?? '' }))} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Type de période *">
          <select className={inputCls} value={form.type} onChange={f('type')}>
            {TARGET_PERIODS.map(t => <option key={t} value={t}>{PERIOD_LABELS[t]}</option>)}
          </select>
        </Field>
        <Field label="Métrique *">
          <select className={inputCls} value={form.targetType} onChange={f('targetType')}>
            {TARGET_TYPES.map(t => <option key={t} value={t}>{TARGET_TYPE_LABELS[t]}</option>)}
          </select>
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Année *">
          <input className={inputCls} type="number" value={form.year} onChange={f('year')} />
        </Field>
        <Field label={form.type === 'MONTHLY' ? 'Mois (1-12) *' : form.type === 'QUARTERLY' ? 'Trimestre (1-4) *' : form.type === 'ANNUAL' ? 'Année (mettre 1) *' : 'Semaine ISO *'}>
          <input className={inputCls} type="number" min="1" max={form.type === 'MONTHLY' ? 12 : form.type === 'QUARTERLY' ? 4 : form.type === 'ANNUAL' ? 1 : 53} value={form.period} onChange={f('period')} />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Valeur cible *">
          <input className={inputCls} type="number" min="0" value={form.targetValue} onChange={f('targetValue')}
            placeholder={form.targetType === 'REVENUE' ? '5000000' : '10'} />
        </Field>
        {form.targetType === 'REVENUE' && (
          <Field label="Devise">
            <select className={inputCls} value={form.currency} onChange={f('currency')}>
              <option value="XOF">XOF</option>
              <option value="EUR">EUR</option>
              <option value="USD">USD</option>
            </select>
          </Field>
        )}
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--tx3)] hover:text-[var(--tx)]">Annuler</button>
        <button onClick={() => mutate()} disabled={isPending || !isValid}
          className="flex items-center gap-2 px-5 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium disabled:opacity-50">
          {isPending ? <Spinner /> : <Plus className="w-4 h-4" />} Créer
        </button>
      </div>
    </Modal>
  )
}

// ============================================================================
//  PAGE SÉQUENCES DRIP
// ============================================================================

export function SequencesPage() {
  const [showCreate, setShowCreate] = useState(false)
  const [selected, setSelected]     = useState<EmailSequence | null>(null)
  const [isActiveFilter, setIsActiveFilter] = useState<string>('')
  const queryClient = useQueryClient()
  const addNotif    = useNotificationStore(s => s.addNotification)
  const { hasPermission } = useAuthStore()
  const canManage = hasPermission('crm:configure')

  const { data, isLoading } = useQuery({
    queryKey: ['crm-sequences', isActiveFilter],
    queryFn:  () => crmApi.listSequences({ isActive: isActiveFilter !== '' ? isActiveFilter === 'true' : undefined }),
  })

  const sequences: EmailSequence[] = data?.items ?? []

  const deleteMutation = useMutation({
    mutationFn: (id: string) => crmApi.deleteSequence(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-sequences'] })
      addNotif({ id: Date.now().toString(), type: 'success', title: 'Séquence supprimée' })
    },
    onError: (e: any) => {
      addNotif({ id: Date.now().toString(), type: 'error', title: e.response?.data?.message ?? 'Erreur suppression' })
    },
  })

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      crmApi.updateSequence(id, { isActive }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['crm-sequences'] }),
  })

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[var(--tx)]">Séquences de relance</h1>
          <p className="text-[var(--tx3)] text-xs mt-0.5">
            Emails automatiques J+3, J+7, J+14 · Mode approbation disponible
          </p>
        </div>
        {canManage && (
          <button onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-xl text-sm font-medium transition-colors">
            <Plus className="w-4 h-4" /> Nouvelle séquence
          </button>
        )}
      </div>

      {/* Filtre */}
      <select value={isActiveFilter} onChange={e => setIsActiveFilter(e.target.value)}
        className="px-3 py-2 nx-card text-sm text-[var(--tx)] focus:outline-none">
        <option value="">Toutes</option>
        <option value="true">Actives</option>
        <option value="false">Inactives</option>
      </select>

      {/* Liste */}
      {isLoading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : sequences.length === 0 ? (
        <div className="bg-[var(--surface)] border border-[var(--bd)] rounded-xl p-12 text-center">
          <Mail className="w-10 h-10 text-[var(--tx4)] mx-auto mb-3" />
          <p className="text-[var(--tx3)] text-sm">Aucune séquence configurée</p>
          {canManage && (
            <button onClick={() => setShowCreate(true)}
              className="mt-4 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm">
              Créer la première séquence
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {sequences.map(seq => (
            <SequenceCard
              key={seq.id}
              seq={seq}
              canManage={canManage}
              onView={() => setSelected(seq)}
              onToggle={() => toggleActiveMutation.mutate({ id: seq.id, isActive: !seq.isActive })}
              onDelete={() => deleteMutation.mutate(seq.id)}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {showCreate && (
        <CreateSequenceModal onClose={() => setShowCreate(false)} onCreated={() => {
          queryClient.invalidateQueries({ queryKey: ['crm-sequences'] })
          setShowCreate(false)
          addNotif({ id: Date.now().toString(), type: 'success', title: 'Séquence créée' })
        }} />
      )}
      {selected && <SequenceDetailModal seq={selected} onClose={() => setSelected(null)} />}
    </div>
  )
}

// ─── Carte séquence ───────────────────────────────────────────────────────────

function SequenceCard({ seq, canManage, onView, onToggle, onDelete }: {
  seq: EmailSequence; canManage: boolean
  onView: () => void; onToggle: () => void; onDelete: () => void
}) {
  const [showMenu, setShowMenu] = useState(false)
  const activeCount = seq._count?.opportunitySequences ?? 0

  return (
    <div className={`bg-[var(--surface-2)] border rounded-xl p-4 transition-all ${seq.isActive ? 'border-[var(--bd)] hover:border-blue-500/30' : 'border-[var(--bd)] opacity-60'}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded-lg ${seq.isActive ? 'bg-[#BAE6FD]' : 'bg-[var(--surface-2)]'}`}>
            <Mail className={`w-4 h-4 ${seq.isActive ? 'text-[var(--blue-d)]' : 'text-[var(--tx3)]'}`} />
          </div>
          <div>
            <p className="text-sm font-semibold text-[var(--tx)] leading-tight">{seq.name}</p>
            {activeCount > 0 && (
              <p className="text-xs text-[var(--green-text)]">{activeCount} opp. active{activeCount > 1 ? 's' : ''}</p>
            )}
          </div>
        </div>

        {canManage && (
          <div className="relative">
            <button onClick={() => setShowMenu(!showMenu)}
              className="p-1.5 text-[var(--tx3)] hover:text-[var(--tx)] rounded-lg hover:bg-[var(--surface-2)]">
              <MoreVertical className="w-4 h-4" />
            </button>
            {showMenu && (
              <div className="absolute right-0 top-7 bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl shadow-xl z-10 min-w-36 py-1" onMouseLeave={() => setShowMenu(false)}>
                <button onClick={() => { onView(); setShowMenu(false) }}
                  className="w-full px-3 py-2 text-xs text-left text-[var(--tx2)] hover:bg-[var(--surface-2)] flex items-center gap-2">
                  <Eye className="w-3.5 h-3.5" /> Voir les détails
                </button>
                <button onClick={() => { onToggle(); setShowMenu(false) }}
                  className="w-full px-3 py-2 text-xs text-left text-[var(--tx2)] hover:bg-[var(--surface-2)] flex items-center gap-2">
                  {seq.isActive ? <Square className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                  {seq.isActive ? 'Désactiver' : 'Activer'}
                </button>
                <hr className="my-1 border-[var(--bd)]" />
                <button onClick={() => { onDelete(); setShowMenu(false) }}
                  className="w-full px-3 py-2 text-xs text-left text-[var(--red-text)] hover:bg-[#F87171]/10 flex items-center gap-2">
                  <Trash2 className="w-3.5 h-3.5" /> Supprimer
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {seq.description && (
        <p className="text-xs text-[var(--tx3)] mb-3 line-clamp-2">{seq.description}</p>
      )}

      {/* Étapes visuelles */}
      <div className="flex items-center gap-1 mb-3">
        {seq.steps.map((step, i) => (
          <div key={step.id} className="flex items-center gap-1">
            <div className="flex flex-col items-center">
              <div className="w-7 h-7 rounded-full bg-[var(--surface-2)] border border-[var(--bd)] flex items-center justify-center">
                <span className="text-[10px] font-bold text-[var(--tx2)]">{i + 1}</span>
              </div>
              <span className="text-[9px] text-[var(--tx3)] mt-0.5">J+{step.delayDays}</span>
            </div>
            {i < seq.steps.length - 1 && <ArrowRight className="w-3 h-3 text-[var(--tx4)] shrink-0" />}
          </div>
        ))}
        {seq.steps.length === 0 && (
          <span className="text-xs text-[var(--tx3)]">Aucune étape configurée</span>
        )}
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-[var(--bd)]">
        <div className="flex items-center gap-1">
          <div className={`w-1.5 h-1.5 rounded-full ${seq.isActive ? 'bg-[var(--green)]' : 'bg-[var(--bd2)]'}`} />
          <span className="text-xs text-[var(--tx3)]">{seq.isActive ? 'Active' : 'Inactive'}</span>
        </div>
        <button onClick={onView} className="text-xs text-[var(--blue-d)] hover:text-blue-300 transition-colors">
          Voir détails →
        </button>
      </div>
    </div>
  )
}

// ─── Modal détail séquence ────────────────────────────────────────────────────

function SequenceDetailModal({ seq: initialSeq, onClose }: { seq: EmailSequence; onClose: () => void }) {
  const { data } = useQuery({
    queryKey: ['crm-sequence', initialSeq.id],
    queryFn:  () => crmApi.getSequence(initialSeq.id),
  })
  const seq: any = data ?? initialSeq
  const opps: any[] = seq.opportunitySequences ?? []

  return (
    <Modal title={seq.name} onClose={onClose} wide>
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full ${seq.isActive ? 'bg-[var(--green)]' : 'bg-[var(--bd2)]'}`} />
        <span className="text-xs text-[var(--tx3)]">{seq.isActive ? 'Active' : 'Inactive'}</span>
        <span className="text-xs text-[var(--tx4)]">·</span>
        <span className="text-xs text-[var(--tx3)]">{seq.steps?.length ?? 0} étape{seq.steps?.length !== 1 ? 's' : ''}</span>
        <span className="text-xs text-[var(--tx4)]">·</span>
        <span className="text-xs text-[var(--tx3)]">{seq._count?.opportunitySequences ?? 0} utilisation{(seq._count?.opportunitySequences ?? 0) !== 1 ? 's' : ''}</span>
      </div>

      {seq.description && <p className="text-sm text-[var(--tx2)]">{seq.description}</p>}

      {/* Timeline des étapes */}
      <div>
        <p className="text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider mb-3">Étapes</p>
        <div className="space-y-3">
          {(seq.steps ?? []).map((step: any, i: number) => (
            <div key={step.id} className="flex gap-3">
              <div className="flex flex-col items-center">
                <div className="w-8 h-8 rounded-full bg-[var(--blue)]/20 border border-blue-500/30 flex items-center justify-center shrink-0">
                  <span className="text-xs font-bold text-[var(--blue-d)]">{i + 1}</span>
                </div>
                {i < (seq.steps?.length ?? 0) - 1 && <div className="w-px flex-1 bg-[var(--surface-2)] mt-1" />}
              </div>
              <div className="flex-1 pb-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold text-[var(--tx)]">{step.subject}</span>
                  <span className="text-[10px] px-2 py-0.5 bg-[var(--surface-2)] text-[var(--tx3)] rounded-full">J+{step.delayDays}</span>
                </div>
                <p className="text-xs text-[var(--tx3)] line-clamp-2">{step.bodyTemplate.replace(/<[^>]+>/g, ' ').trim()}</p>
              </div>
            </div>
          ))}
          {(seq.steps ?? []).length === 0 && (
            <p className="text-sm text-[var(--tx3)]">Aucune étape configurée pour cette séquence.</p>
          )}
        </div>
      </div>

      {/* Opportunités actives */}
      {opps.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider mb-3">
            Opportunités en cours ({opps.length})
          </p>
          <div className="space-y-2">
            {opps.map((os: any) => (
              <div key={os.id} className="flex items-center justify-between p-3 bg-[var(--surface-2)] rounded-lg">
                <div>
                  <p className="text-sm text-[var(--tx)]">{os.opportunity?.title ?? '—'}</p>
                  <p className="text-xs text-[var(--tx3)]">
                    {os.opportunity?.assignedTo?.user?.firstName} {os.opportunity?.assignedTo?.user?.lastName}
                    {' · '}Étape {os.currentStep}/{seq.steps?.length ?? '?'}
                  </p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  os.status === 'ACTIVE' ? 'bg-[var(--green)]/10 text-[var(--green-text)]' :
                  os.status === 'PAUSED' ? 'bg-[var(--amber)]/10 text-[var(--amber-text)]' :
                  'bg-[var(--surface-2)] text-[var(--tx3)]'
                }`}>{os.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-end pt-2">
        <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--tx3)] hover:text-[var(--tx)]">Fermer</button>
      </div>
    </Modal>
  )
}

// ─── Modal création séquence ──────────────────────────────────────────────────

type StepForm = { delayDays: string; subject: string; bodyTemplate: string }

function CreateSequenceModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName]           = useState('')
  const [description, setDesc]    = useState('')
  const [triggerDays, setTrigger] = useState('3,7,14')
  const [steps, setSteps]         = useState<StepForm[]>([
    { delayDays: '3',  subject: 'Suivi de notre échange', bodyTemplate: 'Bonjour {{firstName}},\n\nJe souhaitais faire suite à notre dernier échange concernant {{opportunityTitle}}.\n\nCordialement,' },
    { delayDays: '7',  subject: 'Relance — {{opportunityTitle}}', bodyTemplate: 'Bonjour {{firstName}},\n\nAvez-vous eu l\'occasion de réfléchir à notre proposition ?\n\nCordialement,' },
    { delayDays: '14', subject: 'Dernière relance', bodyTemplate: 'Bonjour {{firstName}},\n\nJe me permets de vous contacter une dernière fois au sujet de {{opportunityTitle}}.\n\nCordialement,' },
  ])

  const addStep = () => setSteps(s => [...s, { delayDays: '', subject: '', bodyTemplate: '' }])
  const removeStep = (i: number) => setSteps(s => s.filter((_, j) => j !== i))
  const updateStep = (i: number, k: keyof StepForm, v: string) =>
    setSteps(s => s.map((st, j) => j === i ? { ...st, [k]: v } : st))

  const { mutate, isPending } = useMutation({
    mutationFn: () => crmApi.createSequence({
      name, description,
      triggerDays: triggerDays.split(',').map(d => parseInt(d.trim())).filter(Boolean),
      steps: steps.map(s => ({
        delayDays:    parseInt(s.delayDays),
        subject:      s.subject,
        bodyTemplate: s.bodyTemplate,
      })),
    }),
    onSuccess: onCreated,
  })

  const isValid = name && steps.every(s => s.delayDays && s.subject && s.bodyTemplate)

  return (
    <Modal title="Nouvelle séquence de relance" onClose={onClose} wide>
      <Field label="Nom de la séquence *">
        <input className={inputCls} value={name} onChange={e => setName(e.target.value)} placeholder="Ex: Séquence standard 14 jours" />
      </Field>
      <Field label="Description">
        <input className={inputCls} value={description} onChange={e => setDesc(e.target.value)} placeholder="Usage, cible…" />
      </Field>
      <Field label="Jours de déclenchement auto (séparés par virgules)">
        <input className={inputCls} value={triggerDays} onChange={e => setTrigger(e.target.value)} placeholder="3,7,14" />
      </Field>

      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider">Étapes ({steps.length})</p>
          <button onClick={addStep}
            className="flex items-center gap-1 text-xs text-[var(--blue-d)] hover:text-blue-300">
            <Plus className="w-3 h-3" /> Ajouter une étape
          </button>
        </div>

        <div className="space-y-3">
          {steps.map((step, i) => (
            <div key={i} className="p-3 bg-[var(--surface-2)] rounded-xl space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-[var(--tx)]">Étape {i + 1}</span>
                {steps.length > 1 && (
                  <button onClick={() => removeStep(i)} className="text-[var(--tx3)] hover:text-[var(--red-text)]">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Field label="Délai (jours) *">
                  <input className={inputCls} type="number" min="1" value={step.delayDays}
                    onChange={e => updateStep(i, 'delayDays', e.target.value)} placeholder="3" />
                </Field>
                <div className="col-span-2">
                  <Field label="Sujet *">
                    <input className={inputCls} value={step.subject}
                      onChange={e => updateStep(i, 'subject', e.target.value)}
                      placeholder="Sujet de l'email (variables: {{firstName}}, {{opportunityTitle}})" />
                  </Field>
                </div>
              </div>
              <Field label="Corps (HTML ou texte, variables supportées) *">
                <textarea className={inputCls} rows={3} value={step.bodyTemplate}
                  onChange={e => updateStep(i, 'bodyTemplate', e.target.value)} />
              </Field>
            </div>
          ))}
        </div>

        <div className="mt-2 p-2 bg-[var(--surface-2)]/30 rounded-lg">
          <p className="text-[11px] text-[var(--tx3)]">
            Variables disponibles : <code className="text-[var(--blue-d)]">{'{{firstName}}'}</code>{' '}
            <code className="text-[var(--blue-d)]">{'{{contactName}}'}</code>{' '}
            <code className="text-[var(--blue-d)]">{'{{opportunityTitle}}'}</code>
          </p>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--tx3)] hover:text-[var(--tx)]">Annuler</button>
        <button onClick={() => mutate()} disabled={isPending || !isValid}
          className="flex items-center gap-2 px-5 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium disabled:opacity-50">
          {isPending ? <Spinner /> : <Zap className="w-4 h-4" />} Créer la séquence
        </button>
      </div>
    </Modal>
  )
}
